/**
 * Sous-composants pour les pages blog. Fichier séparé pour éviter le bug du
 * plugin babel "visual-edits" sur les .map() imbriqués.
 */
import { Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const BlogCard = ({ article }) => (
  <Link
    to={`/blog/${article.slug}`}
    className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-slate-100"
    data-testid={`blog-card-${article.slug}`}
  >
    <div className="aspect-video overflow-hidden bg-slate-100">
      <img
        src={article.image}
        alt={article.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
    </div>
    <div className="p-6">
      <Badge className="bg-red-100 text-red-700 mb-3 hover:bg-red-100">{article.category}</Badge>
      <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-red-600 transition-colors">
        {article.title}
      </h3>
      <p className="text-slate-600 text-sm mb-4 leading-relaxed line-clamp-3">{article.excerpt}</p>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{article.date}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{article.readTime} min</span>
        </div>
        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  </Link>
);

export const ArticleHeader = ({ article }) => (
  <header className="mb-12">
    <Badge className="bg-red-100 text-red-700 mb-4 hover:bg-red-100">{article.category}</Badge>
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
      {article.title}
    </h1>
    <p className="text-lg text-slate-600 leading-relaxed mb-6">{article.excerpt}</p>
    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 pb-6 border-b border-slate-200">
      <span className="flex items-center gap-2"><User className="h-4 w-4" />{article.author}</span>
      <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /><time dateTime={article.date}>{article.date}</time></span>
      <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{article.readTime} min de lecture</span>
    </div>
  </header>
);

const SectionParagraph = ({ content }) => (
  <p className="text-slate-700 leading-relaxed mb-5 text-base md:text-lg">{content}</p>
);

const SectionH2 = ({ content }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-5">{content}</h2>
);

const SectionH3 = ({ content }) => (
  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mt-8 mb-3">{content}</h3>
);

const SectionList = ({ items, ordered }) => {
  if (ordered) {
    return (
      <ol className="list-decimal pl-6 space-y-2 mb-6 text-slate-700 leading-relaxed">
        {items.map(renderListItem)}
      </ol>
    );
  }
  return (
    <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700 leading-relaxed">
      {items.map(renderListItem)}
    </ul>
  );
};

const renderListItem = (item) => <li key={item}>{item}</li>;

const SectionNote = ({ content }) => (
  <aside className="bg-amber-50 border-l-4 border-amber-500 p-5 my-6 rounded-r-lg">
    <p className="text-slate-800 leading-relaxed font-medium">{content}</p>
  </aside>
);

const SectionCta = ({ data }) => (
  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 my-10 text-center">
    <h3 className="text-2xl font-bold text-white mb-3">{data.title}</h3>
    <p className="text-slate-300 mb-6 leading-relaxed">{data.text}</p>
    <div className="flex flex-col sm:flex-row justify-center gap-3">
      <a href="tel:+33549057490">
        <Button size="lg" className="bg-red-600 hover:bg-red-700 gap-2">
          <Phone className="h-5 w-5" /> 05 49 05 74 90
        </Button>
      </a>
      <Link to={data.link}>
        <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 gap-2">
          {data.linkText} <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  </div>
);

export const ArticleBlock = ({ block }) => {
  const blockType = block.type;
  const blockContent = block.content;
  if (blockType === "p") return <SectionParagraph content={blockContent} />;
  if (blockType === "h2") return <SectionH2 content={blockContent} />;
  if (blockType === "h3") return <SectionH3 content={blockContent} />;
  if (blockType === "ul") return <SectionList items={blockContent} ordered={false} />;
  if (blockType === "ol") return <SectionList items={blockContent} ordered={true} />;
  if (blockType === "note") return <SectionNote content={blockContent} />;
  if (blockType === "cta") return <SectionCta data={blockContent} />;
  return null;
};
